import React, { useCallback, useMemo } from 'react';

import type { ParagraphProps, ToggleableKeys, VerticalAlign } from '@aline/common';
import { DECLARATIVE_MENU_SEPARATOR } from 'src/components/declarative-menu';
import type { DeclarativeMenuConfig } from 'src/components/declarative-menu';
import { BoldIcon } from 'src/components/icons/BoldIcon';
import { CenterAlignIcon } from 'src/components/icons/CenterAlignIcon';
import { ClearFormatIcon } from 'src/components/icons/ClearFormatIcon';
import { DecreaseIndentIcon } from 'src/components/icons/DecreaseIndentIcon';
import { IncreaseIndentIcon } from 'src/components/icons/IncreaseIndentIcon';
import { ItalicIcon } from 'src/components/icons/ItalicIcon';
import { LeftAlignIcon } from 'src/components/icons/LeftAlignIcon';
import { LineSpacingIcon } from 'src/components/icons/LineSpacingIcon';
import { RightAlignIcon } from 'src/components/icons/RightAlignIcon';
import { StrikethroughIcon } from 'src/components/icons/StrikethroughIcon';
import { SubscriptIcon } from 'src/components/icons/SubscriptIcon';
import { SuperscriptIcon } from 'src/components/icons/SuperscriptIcon';
import { UnderlineIcon } from 'src/components/icons/UnderlineIcon';
import { useEditorTabStateActions } from 'src/editor/editor-tab-state-registry';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { editorHotkeys } from 'src/layout-engine';
import { useEditor } from 'src/layout-engine/hooks';
import { useCurrentEditingMode } from 'src/store/document/selectors';

import { plannedOnly, useIfEditableMixin } from '../shared/menu-mixins';
import { useLineSpacingMenu } from '../shared/use-line-spacing-menu';
import { useParagraphStylesMenu } from '../shared/use-paragraph-styles-menu';

export function useFormatMenu() {
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const currentUserId = useCurrentUserId();
  const editingMode = useCurrentEditingMode();
  const {
    documentUi: { setHeaderFooterDialogOpen, setIndentationDialogOpen },
  } = useEditorTabStateActions();
  const editor = useEditor();
  const ifEditable = useIfEditableMixin();
  const paragraphStylesMenu = useParagraphStylesMenu();
  const lineSpacingMenu = useLineSpacingMenu();

  const decreaseFontSize = useCallback(() => {
    if (!currentUserId || !editingMode) return;
    AuthorizedTransforms.decrementFontSize(editor, { authorId: currentUserId, editingMode });
  }, [AuthorizedTransforms, currentUserId, editingMode, editor]);

  const decreaseIndent = useCallback(() => {
    if (!currentUserId || !editingMode) return;
    AuthorizedTransforms.decreaseIndentation(editor, { authorId: currentUserId, editingMode });
  }, [AuthorizedTransforms, currentUserId, editingMode, editor]);

  const headersAndFooters = useCallback(
    () => setHeaderFooterDialogOpen(true),
    [setHeaderFooterDialogOpen]
  );

  const increaseFontSize = useCallback(() => {
    if (!currentUserId || !editingMode) return;
    AuthorizedTransforms.incrementFontSize(editor, { authorId: currentUserId, editingMode });
  }, [AuthorizedTransforms, currentUserId, editingMode, editor]);

  const increaseIndent = useCallback(() => {
    if (!currentUserId || !editingMode) return;
    AuthorizedTransforms.increaseIndentation(editor, { authorId: currentUserId, editingMode });
  }, [AuthorizedTransforms, currentUserId, editingMode, editor]);

  const indentationOptions = useCallback(
    () => setIndentationDialogOpen(true),
    [setIndentationDialogOpen]
  );

  const setParagraphProps = useCallback(
    (props: Partial<ParagraphProps>) => () => {
      if (!currentUserId || !editingMode) return;
      AuthorizedTransforms.setParagraphProps(editor, editingMode, currentUserId, props);
    },
    [AuthorizedTransforms, currentUserId, editingMode, editor]
  );

  const toggleTextFormatting = useCallback(
    (formatKey: ToggleableKeys) => () => {
      if (!currentUserId || !editingMode) return;
      AuthorizedTransforms.toggleTextFormatting(editor, {
        authorId: currentUserId,
        editingMode,
        formatKey,
      });
    },
    [AuthorizedTransforms, currentUserId, editingMode, editor]
  );

  const toggleVerticalAlign = useCallback(
    (verticalAlign: VerticalAlign) => () => {
      if (!currentUserId || !editingMode) return;
      AuthorizedTransforms.toggleVerticalAlign(editor, {
        authorId: currentUserId,
        editingMode,
        verticalAlign,
      });
    },
    [AuthorizedTransforms, currentUserId, editingMode, editor]
  );

  return useMemo(
    (): DeclarativeMenuConfig => [
      {
        label: 'Text',
        submenu: [
          {
            hint: editorHotkeys.bold.hint,
            icon: <BoldIcon />,
            label: 'Bold',
            onClick: toggleTextFormatting('bold'),
            ...ifEditable,
          },
          {
            hint: editorHotkeys.italicize.hint,
            icon: <ItalicIcon />,
            label: 'Italic',
            onClick: toggleTextFormatting('italic'),
            ...ifEditable,
          },
          {
            hint: editorHotkeys.underline.hint,
            icon: <UnderlineIcon />,
            label: 'Underline',
            onClick: toggleTextFormatting('underline'),
            ...ifEditable,
          },
          {
            hint: editorHotkeys.strikethrough.hint,
            icon: <StrikethroughIcon />,
            label: 'Strikethrough',
            onClick: toggleTextFormatting('strikethrough'),
            ...ifEditable,
          },
          {
            hint: editorHotkeys.superscript.hint,
            icon: <SuperscriptIcon />,
            label: 'Superscript',
            onClick: toggleVerticalAlign('superscript'),
            ...ifEditable,
          },
          {
            hint: editorHotkeys.subscript.hint,
            icon: <SubscriptIcon />,
            label: 'Subscript',
            onClick: toggleVerticalAlign('subscript'),
            ...ifEditable,
          },
          DECLARATIVE_MENU_SEPARATOR,
          {
            label: 'Size',
            submenu: [
              {
                hint: editorHotkeys.increaseFontSize.hint,
                label: 'Increase font size',
                onClick: increaseFontSize,
                ...ifEditable,
              },
              {
                hint: editorHotkeys.decreaseFontSize.hint,
                label: 'Decrease font size',
                onClick: decreaseFontSize,
                ...ifEditable,
              },
            ],
          },
        ],
      },
      { label: 'Paragraph styles', submenu: paragraphStylesMenu },
      {
        label: 'Align and indent',
        submenu: [
          {
            hint: editorHotkeys.alignLeft.hint,
            icon: <LeftAlignIcon />,
            label: 'Left',
            onClick: setParagraphProps({ alignment: 'left' }),
            ...ifEditable,
          },
          {
            hint: editorHotkeys.alignCenter.hint,
            icon: <CenterAlignIcon />,
            label: 'Center',
            onClick: setParagraphProps({ alignment: 'center' }),
            ...ifEditable,
          },
          {
            hint: editorHotkeys.alignRight.hint,
            icon: <RightAlignIcon />,
            label: 'Right',
            onClick: setParagraphProps({ alignment: 'right' }),
            ...ifEditable,
          },
          DECLARATIVE_MENU_SEPARATOR,
          {
            hint: editorHotkeys.increaseIndentation.hint,
            icon: <IncreaseIndentIcon />,
            label: 'Increase indent',
            onClick: increaseIndent,
            ...ifEditable,
          },
          {
            hint: editorHotkeys.decreaseIndentation.hint,
            icon: <DecreaseIndentIcon />,
            label: 'Decrease indent',
            onClick: decreaseIndent,
            ...ifEditable,
          },
          DECLARATIVE_MENU_SEPARATOR,
          { label: 'Indentation options...', onClick: indentationOptions, ...ifEditable },
        ],
      },
      { icon: <LineSpacingIcon />, label: 'Line spacing', submenu: lineSpacingMenu },
      DECLARATIVE_MENU_SEPARATOR,
      { label: 'Headers and footers...', onClick: headersAndFooters, ...ifEditable },
      DECLARATIVE_MENU_SEPARATOR,
      {
        hint: editorHotkeys.clearFormatting.hint,
        icon: <ClearFormatIcon />,
        label: 'Clear formatting',
        ...ifEditable,
        ...plannedOnly,
      },
    ],
    [
      decreaseFontSize,
      decreaseIndent,
      headersAndFooters,
      ifEditable,
      increaseFontSize,
      increaseIndent,
      indentationOptions,
      lineSpacingMenu,
      paragraphStylesMenu,
      setParagraphProps,
      toggleTextFormatting,
      toggleVerticalAlign,
    ]
  );
}
